import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { getCurrentUser, fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import { useAppDispatch } from '@/state/hooks';
import { setAuth, logOut } from '@/state/reducers/auth.slice';
import AuthService from '@/lib/auth';
import biometricAuthService from '@/services/biometricAuthService';
import { decodeToken } from '@/lib/api';
import { logger } from '@/lib/logger';
import { isStaffRole } from '@/utils/mfaEmail';

interface ProtectedRouteProps {
  children?: React.ReactNode;
  redirectTo?: string;
  isPublic?: boolean;
  requiredRoles?: string[];
}

export default function ProtectedRoute({
  children,
  redirectTo = '/login',
  isPublic = false,
  requiredRoles = []
}: ProtectedRouteProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const isAuthPage = ['/login', '/auth/register', '/auth/forgot-password'].includes(location.pathname);

  useEffect(() => {
    let isMounted = true;

    const checkAuth = async () => {
      try {
        let currentUser;
        try {
          currentUser = await getCurrentUser();
        } catch {
          currentUser = null;
        }

        if (!isMounted) return;

        const isAuthenticated = !!currentUser;

        setAuthChecked(true);
        setIsLoading(false);

        if (isAuthenticated && currentUser) {
          const session = await fetchAuthSession();
          const userAttributes = await fetchUserAttributes();
          // Use username instead of userId for RDS migration
          const email = userAttributes.email || currentUser.username || '';

          const accessToken = session.tokens?.accessToken?.toString() || '';
          const idToken = session.tokens?.idToken?.toString() || '';

          // TOKEN EXCHANGE: Ensure we have a backend-trusted token
          let backendToken = localStorage.getItem('jwt_token');

          // If no backend token exists, or it's the same as Cognito, OR it's a generic Admin token
          const decoded = backendToken ? decodeToken(backendToken) : null;
          const isAdminToken = decoded?.payload?.sub === "admin-MnTrHealthAdmin";

          if (!backendToken || backendToken === accessToken || backendToken === idToken || isAdminToken) {
            logger.debug(isAdminToken ? '🔄 ProtectedRoute: Admin token detected, forced exchange...' : '🔄 ProtectedRoute: Performing token exchange...');
            try {
              // IMPORTANT: Using user.username as it represents the migrated auth_uuid/identifier
              const result = await biometricAuthService.login(currentUser.username, true);
              if (result.success && result.data?.access_token) {
                backendToken = result.data.access_token;
                logger.debug('✅ ProtectedRoute: Token exchange successful');
              } else {
                logger.warn('⚠️ ProtectedRoute: Token exchange returned unsuccessful status');
              }
            } catch (err) {
              logger.error('❌ ProtectedRoute: Token exchange failed', err);
            }
          }

          const tokenToUse = backendToken || accessToken;

          if (requiredRoles.length > 0) {
            let staffUser: { role?: string } | null = null;
            try {
              staffUser = await AuthService.getProfile(email); // Use email for safer lookup
            } catch {
              staffUser = null;
            }
            const userRole = staffUser?.role || userAttributes['custom:role'] || userAttributes.role;
            
            // Normalize roles by removing spaces and lowercase for robust matching
            const normalize = (s: string | undefined) => (s || "").toLowerCase().replace(/\s/g, "");
            const normalizedUserRole = normalize(userRole);
            
            const hasRequiredRole = requiredRoles.some((r) => normalize(r) === normalizedUserRole);
            
            if (!hasRequiredRole) {
              console.warn(`🔒 Access Denied: User role "${userRole}" does not match any required roles: ${requiredRoles.join(', ')}`);
              navigate('/dashboard');
              return;
            }
          }

          let staffUser: { id?: string; auth_uuid?: string; name?: string; email?: string; role?: string } | null = null;
          try {
            staffUser = await AuthService.getProfile(email);
          } catch {
            staffUser = null;
          }

          if (!staffUser && currentUser.username) {
            try {
              staffUser = await AuthService.getProfile(currentUser.username);
            } catch {
              staffUser = null;
            }
          }

          const effectiveRole = staffUser?.role || userAttributes['custom:role'] || userAttributes.role || null;

          // MFA Verification Requirement:
          // Strictly enforced for all staff roles (Doctor, Nurse, Admin, etc.)
          // Explicitly skipped for Patients so patient workflows remain uninterrupted.
          if (isStaffRole(effectiveRole)) {
            const mfaVerified = sessionStorage.getItem('mfa_verified') === 'true';
            if (!mfaVerified && !isAuthPage) {
              logger.warn('🔒 ProtectedRoute: Staff MFA verification required for role:', effectiveRole);
              const from = location.pathname !== '/login' ? location.pathname + location.search : '/';
              navigate(redirectTo, { state: { from }, replace: true });
              return;
            }
          }

          dispatch(setAuth({
            token: tokenToUse,
            user: {
              id: staffUser?.auth_uuid || currentUser.username, // Priority to DB identifier (username in this pool)
              email: staffUser?.email || email,
              name: staffUser?.name || userAttributes.name || email?.split('@')[0] || 'User',
              role: staffUser?.role || userAttributes['custom:role'] || null
            },
            expiresIn: '3600'
          }));

          if (isAuthPage) {
            navigate('/dashboard', { replace: true });
          }
        } else {
          // If not authenticated and not on an auth page, redirect to login
          console.log('Not authenticated, redirecting to login');
          const from = location.pathname !== '/login' ? location.pathname + location.search : '/';
          navigate(redirectTo, { state: { from }, replace: true });
        }
      } catch (error) {
        console.error('Auth check error:', error);
        if (isMounted) {
          setAuthChecked(true);
          setIsLoading(false);
          if (!isAuthPage) {
            navigate(redirectTo, { replace: true });
          }
        }
      }
    };

    checkAuth();

    const hubListener = Hub.listen('auth', ({ payload }) => {
      if (!isMounted) return;

      // Nuclear guard: block everything if we're on forgot-password
      if (window.location.pathname.includes('forgot-password')) {
        return;
      }

      const { event } = payload;
      setAuthChecked(true);
      setIsLoading(false);
      if (event === 'signedIn') {
        checkAuth();
        if (isAuthPage) navigate('/dashboard', { replace: true });
      } else if (event === 'signedOut') {
        dispatch(logOut());
        if (!isAuthPage) {
          const from = location.pathname !== '/login' ? location.pathname + location.search : '/';
          navigate(redirectTo, { state: { from }, replace: true });
        }
      }
    });

    return () => {
      isMounted = false;
      hubListener();
    };
  }, [dispatch, isAuthPage, location.pathname, location.search, navigate, redirectTo]);

  // For public routes, just render the children
  if (isPublic) {
    return <>{children}</>;
  }

  // Show loading state
  if (isLoading || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // Profile requirement check removed as it was causing build issues
  // and wasn't being used in the application

  // If we get here, the user is authenticated and can access the route
  return <>{children}</>;
}